import { captureException } from "@lib/telemetry/sentry";

type AssertContext = Record<string, unknown> | (() => Record<string, unknown>);

const FLOATING_POINT_TOLERANCE_MULTIPLIER = 8;

export function assert(
  condition: boolean,
  message?: string | (() => string),
  context?: AssertContext,
) {
  if (!condition) {
    const messageStr =
      typeof message === "function"
        ? message()
        : (message ?? "Assertion failed");
    const error = new Error(messageStr);
    const nodeEnv = process.env.NODE_ENV ?? "";
    if (["development", "test"].includes(nodeEnv)) {
      throw error;
    } else {
      const extra = typeof context === "function" ? context() : context;
      captureException(error, extra == null ? undefined : { extra });
    }
  }
}

/**
 * Compare values after floating-point arithmetic while allowing only rounding
 * noise at the scale of the operands. Domain-level tolerances should be
 * handled separately by the caller.
 */
export function isLessThanOrEqualWithinFloatingPointTolerance(
  value: number,
  limit: number,
): boolean {
  if (!Number.isFinite(value) || !Number.isFinite(limit)) {
    return false;
  }
  if (value <= limit) {
    return true;
  }

  const scale = Math.max(1, Math.abs(value), Math.abs(limit));
  const tolerance =
    FLOATING_POINT_TOLERANCE_MULTIPLIER * Number.EPSILON * scale;
  return value - limit <= tolerance;
}

export function interleave<A>(a: A[], b: A[]): A[] {
  assert(a.length === b.length, "interleave expects arrays of equal length");
  const result: A[] = [];
  for (let i = 0; i < a.length; i++) {
    result.push(a[i]!);
    result.push(b[i]!);
  }
  return result;
}

export function roundThousandths(n: number): number {
  return Math.round(n * 1000) / 1000;
}

export function roundToNearestThousand(amount: number): number {
  return Math.round(amount / 1000) * 1000;
}

export function dedupe<T>(array: T[]): T[] {
  return [...new Set(array)];
}

export function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/** Build a word -> count frequency map from text. */
export function wordBag(text: string): Map<string, number> {
  const bag = new Map<string, number>();
  for (const w of text.toLowerCase().split(/\s+/)) {
    if (w) bag.set(w, (bag.get(w) ?? 0) + 1);
  }
  return bag;
}

/** Sum of absolute frequency differences between two word bags. */
export function wordBagDiff(
  a: Map<string, number>,
  b: Map<string, number>,
): number {
  let diff = 0;
  for (const [w, count] of a) diff += Math.abs(count - (b.get(w) ?? 0));
  for (const [w, count] of b) if (!a.has(w)) diff += count;
  return diff;
}
